import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import thunkActions from '../actions/thunkActions';
import errorHandler from '../../../common/service/errorHandler';
import initialState from '../initialState';
import IAccessory from '../../model/accessories/IAccessory';
import IAccessoriesCategory from '../../model/accessories/IAccessoriesCategory';
import IAccessoryPack from '../../model/accessories/IAccessoryPack';
import IExtendedAccessoryPack from '../../model/accessories/IExtendedAccessoryPack';
import IExtendedAccessory from '../../model/accessories/IExtendedAccessory';

export const getCurrentCategory = (
  accessoriesCategories: IAccessoriesCategory[],
  currentAccessory: Record<string, IAccessory>,
  key: string,
): IAccessoriesCategory => {
  return accessoriesCategories.find(category => category.name === currentAccessory[key].group);
};

export const getExistingAccessoryCategories = (
  accessoriesCategories: IAccessoriesCategory[],
  currentAccessory: Record<string, IAccessory>,
  key: string,
): IAccessoriesCategory[] => {
  const currentCategory = getCurrentCategory(accessoriesCategories, currentAccessory, key);
  const currentCategoryIndex = accessoriesCategories.indexOf(currentCategory);

  accessoriesCategories[currentCategoryIndex] = {
    ...accessoriesCategories[currentCategoryIndex],
    accessories: [...accessoriesCategories[currentCategoryIndex].accessories, { ...currentAccessory[key], id: key }],
  };

  return accessoriesCategories;
};

export const getInitialAccessoryCategories = (
  accessoriesCategories: IAccessoriesCategory[],
  currentAccessory: Record<string, IAccessory>,
  key: string,
) => {
  const updatedCategory = {
    name: currentAccessory[key].group,
    accessories: [{ ...currentAccessory[key], selected: false, id: key }],
  };

  return [...accessoriesCategories, updatedCategory];
};

const getProcessedAccessoriesCategories = (currentAccessory: Record<string, IAccessory>): IAccessoriesCategory[] => {
  let currentAccessoryItemsCategories: IAccessoriesCategory[] = [];
  Object.keys(currentAccessory).forEach(key => {
    const currentAccessoryItems = getCurrentCategory(currentAccessoryItemsCategories, currentAccessory, key);
    if (!currentAccessoryItems) {
      currentAccessoryItemsCategories = getInitialAccessoryCategories(
        currentAccessoryItemsCategories,
        currentAccessory,
        key,
      );

      return;
    }

    currentAccessoryItemsCategories = getExistingAccessoryCategories(
      currentAccessoryItemsCategories,
      currentAccessory,
      key,
    );
  });

  return currentAccessoryItemsCategories;
};

export const getCurrentSelectedCategories = (
  accessoriesCategories: IAccessoriesCategory[],
  carOptions: string[],
): IAccessoriesCategory[] => {
  return accessoriesCategories.map(category => {
    const updatedAccessories = category.accessories.map(accessory => {
      if (carOptions.includes(accessory.id)) {
        return {
          ...accessory,
          selected: true,
        };
      }

      return accessory;
    });

    return { ...category, accessories: updatedAccessories };
  });
};

export const getInitialAccessory = (accessory: IExtendedAccessory, key: string): IExtendedAccessory => {
  return { ...accessory, id: key };
};

export const setInitialPack = (currentPack: IAccessoryPack, key: string): IExtendedAccessoryPack => {
  return { ...currentPack, selected: false, id: key };
};

export const getProcessedAccessoryPacks = (packs: Record<string, IAccessoryPack>): IExtendedAccessoryPack[] => {
  const extendedAccessoryPacks: IExtendedAccessoryPack[] = [];
  Object.keys(packs).forEach(key => {
    const extendedPack: IExtendedAccessoryPack = setInitialPack(packs[key], key);
    extendedPack.accessories = Object.entries(extendedPack.accessories).map(([key, accessory]) =>
      getInitialAccessory(accessory, key),
    );

    extendedAccessoryPacks.push(extendedPack);
  });

  return extendedAccessoryPacks;
};

const optionsReducer = createSlice({
  name: 'OptionsReducer',
  initialState: initialState.optionsInitialState,
  reducers: {
    setAccessoriesCategories: (state, { payload: accessoriesCategories }) => {
      state.accessoriesCategories = accessoriesCategories;
    },
    processAccessoriesCategories: (state, { payload: carOptionsIds }: PayloadAction<string[]>) => {
      const currentAccessory = current(state).options.accessories.items;
      let accessoriesCategories = getProcessedAccessoriesCategories(currentAccessory);
      accessoriesCategories = getCurrentSelectedCategories(accessoriesCategories, carOptionsIds);

      state.accessoriesCategories = accessoriesCategories;
    },
    processAccessoryPacks: state => {
      const packs = current(state).options.accessoryPacks.items;
      const processedPacks = getProcessedAccessoryPacks(packs);
      state.accessoryPacks = processedPacks;
    },
  },
  extraReducers: builder => {
    builder.addCase(thunkActions.fetchOptions.pending, state => {
      return { ...state, loading: true };
    });
    builder.addCase(thunkActions.fetchOptions.fulfilled, (state, action) => {
      return { ...state, loading: false, options: action.payload, error: null };
    });
    builder.addCase(thunkActions.fetchOptions.rejected, (state, action) => {
      if (errorHandler.handleError(action.error) === false) {
        return {
          ...initialState.optionsInitialState,
          loading: false,
          error: action.error,
        };
      } else {
        return state;
      }
    });
  },
});

export const optionsActions = optionsReducer.actions;

export default optionsReducer.reducer;
